import { Component, computed, inject, OnInit, signal } from "@angular/core";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { TranslateTokenPipe } from "../../../core/pipes";
import { AdaaHelper } from "../../../core/utils";
import { FormActionButtonsComponent } from "../../../shared/components";
import { GroupedSection, StrategyDashboardManagementRule } from "../../../shared/models";
import { StrategyDashboardManagementApiService, SystemLayoutService } from "../../../shared/services";

@Component({
  selector: "adaa-strategy-dashboard-management",
  imports: [TranslatePipe, TranslateTokenPipe, FormActionButtonsComponent],
  templateUrl: "./strategy-dashboard-management.html",
  styleUrl: "./strategy-dashboard-management.scss",
})
export class StrategyDashboardManagementComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _dashboardApiService = inject(StrategyDashboardManagementApiService);

  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();

  rules = signal<StrategyDashboardManagementRule[]>([]);

  years = computed(() => {
    const years = this.rules().map((e) => e.year);
    return [...new Set(years)].sort((a, b) => a - b);
  });

  sections = computed(() => {
    const map = new Map<number, GroupedSection>();

    for (const rule of this.rules()) {
      if (!map.has(rule.sectionId)) {
        map.set(rule.sectionId, {
          sectionId: rule.sectionId,
          sectionNameAE: rule.sectionNameAE,
          sectionNameEN: rule.sectionNameEN,
          years: [],
        });
      }
      map.get(rule.sectionId)!.years.push(rule);
    }

    return Array.from(map.values());
  });

  public ngOnInit() {
    this._refreshPage();
    this._getRules();
  }

  public isSelectAllChecked(year: number): boolean {
    const currentRules = this.rules();
    const rulesForYear = currentRules.filter((rule) => rule.year === year);
    return rulesForYear.every((rule) => rule.isVisible);
  }

  public selectAll(event: Event, year: number) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    const currentRules = this.rules();

    for (const rule of currentRules) {
      if (rule.year === year) {
        rule.isVisible = isChecked;
      }
    }

    this.rules.update(() => [...currentRules]);
  }

  public ruleValueChanged(event: Event, rule: StrategyDashboardManagementRule) {
    const target = event.target as HTMLInputElement;
    rule.isVisible = target.checked;

    const currentRules = this.rules();

    const index = currentRules.findIndex((e) => e.id === rule.id);
    if (index === -1) return;

    currentRules[index] = rule;

    this.rules.update(() => [...currentRules]);
  }

  public submit() {
    this._dashboardApiService.update(this.rules()).subscribe({
      next: () => {
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.goBack();
      },
    });
  }

  public goBack() {
    return this._router.navigateByUrl("/console/government-scores");
  }

  private _refreshPage() {
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe(() => this.goBack());
  }

  private _getRules() {
    this._dashboardApiService.getAll().subscribe({
      next: (response) => {
        this.rules.set(response.responseData ?? []);
      },
    });
  }
}
